import { Box, Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CircleIcon from "@mui/icons-material/Circle";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LanguageStatus } from "@shared/vod-localised";
import { tokens } from "../theme";

/**
 * The language strip both Manage panels wear: a tab per language carrying its
 * own completeness, plus a way to add one.
 *
 * Shared because the two panels answer the same question about the same
 * languages - text for a language, images for a language - and a title whose
 * two editors disagreed about which languages exist would be unexplainable.
 */

/**
 * Languages a title can be offered in.
 *
 * ⚠️ **Lowercase, 2-3 letters, no region** - and that is a fact off the schema,
 * not a style choice. Every `language` and `country` column in this catalogue is
 * `VarChar(3)`, so the shipped model's UPPERCASE-with-region rule (`"PT-BR"`)
 * could never have been stored at all. The tabs display them uppercased; what
 * travels is what is written here. See `canonicalAssetLanguage`.
 */
export const AVAILABLE_LANGUAGES = [
  "sv",
  "en",
  "no",
  "da",
  "fi",
  "de",
  "fr",
  "es",
] as const;

export function statusIcon(status: LanguageStatus) {
  if (status === "done") {
    return <CheckCircleIcon sx={{ fontSize: 15, color: tokens.brand }} />;
  }
  if (status === "partial") {
    return <ErrorIcon sx={{ fontSize: 15, color: tokens.error }} />;
  }
  return <CircleIcon sx={{ fontSize: 12, color: tokens.divider }} />;
}

export default function LanguageTabs({
  languages,
  value,
  statuses,
  onChange,
  onAdd,
}: {
  languages: string[];
  value: string;
  /** Per-language completeness, so a tab can show it without being asked. */
  statuses: Record<string, LanguageStatus>;
  onChange: (next: string) => void;
  onAdd: (language: string) => void;
}) {
  const { t } = useTranslation("templates");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const addable = AVAILABLE_LANGUAGES.filter((l) => !languages.includes(l));

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ borderBottom: `1px solid ${tokens.divider}`, mb: 3 }}
    >
      <Stack direction="row" spacing={2.5}>
        {languages.map((l) => {
          const active = l === value;
          return (
            <Box
              key={l}
              role="tab"
              aria-selected={active}
              tabIndex={0}
              onClick={() => onChange(l)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(l);
                }
              }}
              data-testid={`lang-tab-${l}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                pb: 1,
                cursor: "pointer",
                typography: "bodySm",
                fontWeight: "fontWeightSemibold",
                color: active ? tokens.brand : tokens.inkMuted,
                borderBottom: "2px solid",
                borderColor: active ? tokens.brand : "transparent",
                // Sits on top of the strip's own border so the two do not
                // stack into a 3px line under the active tab.
                mb: "-1px",
              }}
            >
              {l.toUpperCase()}
              {statusIcon(statuses[l] ?? "missing")}
            </Box>
          );
        })}
      </Stack>

      {addable.length > 0 && (
        <Button
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          data-testid="lang-add"
          sx={{ typography: "bodySm", minHeight: 0, pb: 1 }}
        >
          {t("languageTabs.addLanguage")}
        </Button>
      )}
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
      >
        {addable.map((l) => (
          <MenuItem
            key={l}
            onClick={() => {
              onAdd(l);
              setAnchor(null);
            }}
          >
            <Typography variant="bodySm">{l.toUpperCase()}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
